// @flow
import * as React from 'react';
import {defineMessage} from 'react-intl';
import samplePaymentIntent from '../../responses/payment_intent';
import {DefaultCards} from '../../../../components/left-col';

import type {StepsConfig} from '../../../../data/GuideTypes';

export default ([
  {
    file: 'server',
    id: 'setup',
    section: 'setup-stripe',
  },
  {
    file: 'server',
    id: 'create',
    section: 'setup-stripe',
    heading: {
      message: defineMessage({
        id: 'docsShell.integrationBuilder.aap.web.html.steps.create.heading',
        defaultMessage: 'Create a PaymentIntent',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsShell.integrationBuilder.aap.web.html.steps.create.body',
        defaultMessage: `Add an endpoint on your server that creates a <a>PaymentIntent</a>. A PaymentIntent tracks the customer's payment lifecycle, keeping track of any failed payment attempts and ensuring the customer is only charged once. Return the PaymentIntent's client secret in the response to finish the payment on the client.`,
      }),
      values: {
        a: (str) => (
          <a href="/docs/api/payment_intents" target="_blank">
            {str}
          </a>
        ),
      },
    },
    response: samplePaymentIntent,
  },
  {
    id: 'load-stripe',
    section: 'build-checkout-page',
    heading: {
      message: defineMessage({
        id:
          'docsShell.integrationBuilder.aap.web.html.steps.load-stripe.heading',
        defaultMessage: 'Load Stripe.js',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsShell.integrationBuilder.aap.web.html.steps.load-stripe.body',
        defaultMessage:
          'Use Stripe.js to remain PCI compliant by ensuring that card details are sent directly to Stripe without hitting your server. Always load Stripe.js from js.stripe.com to remain compliant. Do not include the script in a bundle or host it yourself.',
      }),
    },
  },
  {
    id: 'build-form',
    section: 'build-checkout-page',
    heading: {
      message: defineMessage({
        id:
          'docsShell.integrationBuilder.aap.web.html.steps.build-form.heading',
        defaultMessage: 'Define the payment form',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsShell.integrationBuilder.aap.web.html.steps.build-form.body',
        defaultMessage:
          'Add an empty placeholder <code>div</code> to your checkout form. Stripe inserts an iframe into this <code>div</code> that securely collects card information.',
      }),
      values: {
        code: (str) => <code>{str}</code>,
      },
    },
  },
  {
    id: 'init-stripe',
    section: 'build-checkout-page',
    heading: {
      message: defineMessage({
        id:
          'docsShell.integrationBuilder.aap.web.html.steps.init-stripe.heading',
        defaultMessage: 'Initialize Stripe.js',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsShell.integrationBuilder.aap.web.html.steps.init-stripe.body',
        defaultMessage:
          'Initialize Stripe.js with your publishable API keys. You will use Stripe.js to create the card input field and complete the payment on the client.',
      }),
    },
  },
  {
    id: 'fetch-pi',
    section: 'build-checkout-page',
    heading: {
      message: defineMessage({
        id: 'docsShell.integrationBuilder.aap.web.html.steps.fetch-pi.heading',
        defaultMessage: 'Fetch a PaymentIntent',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsShell.integrationBuilder.aap.web.html.steps.fetch-pi.body',
        defaultMessage:
          'Immediately make a request to the endpoint on your server to create a new PaymentIntent as soon as the page loads.',
      }),
    },
  },
  {
    id: 'init-elements',
    section: 'build-checkout-page',
    heading: {
      message: defineMessage({
        id:
          'docsShell.integrationBuilder.aap.web.html.steps.init-elements.heading',
        defaultMessage: 'Initialize Stripe Elements',
      }),
    },
    body: {
      message: defineMessage({
        id:
          'docsShell.integrationBuilder.aap.web.html.steps.init-elements.body',
        defaultMessage:
          'Initialize the <a>Stripe Elements UI library</a>. Elements manages the UI components you need to collect card details.',
      }),
      values: {
        a: (str) => (
          <a href="/docs/js/elements_object/create" target="_blank">
            {str}
          </a>
        ),
      },
    },
  },
  {
    id: 'style-element',
    section: 'build-checkout-page',
    heading: {
      message: defineMessage({
        id:
          'docsShell.integrationBuilder.aap.web.html.steps.style-element.heading',
        defaultMessage: 'Optional: Style the checkout form',
      }),
    },
    tabs: {
      type: 'editor',
      active: 'input',
      options: {
        input: {
          type: 'input',
          label: {
            message: defineMessage({
              id:
                'docsShell.integrationBuilder.aap.web.html.steps.style-element.tabs.input.label',
              defaultMessage: 'Input fields',
            }),
          },
        },
        button: {
          type: 'button',
          label: {
            message: defineMessage({
              id:
                'docsShell.integrationBuilder.aap.web.html.steps.style-element.tabs.button.label',
              defaultMessage: 'Button',
            }),
          },
        },
      },
    },
    body: {
      message: defineMessage({
        id:
          'docsShell.integrationBuilder.aap.web.html.steps.style-element.body',
        defaultMessage:
          "Customize the card input by passing a <styleA>style</styleA> object. Use your company's color scheme and font to make it match with the rest of your checkout page. Use custom fonts (e.g. from Google Fonts) by initializing Elements with a <fontSetA>font set</fontSetA>.",
      }),
      values: {
        styleA: (str) => (
          <a target="_blank" href="https://stripe.com/docs/js/appendix/style">
            {str}
          </a>
        ),
        fontSetA: (str) => (
          <a href="/docs/js/elements_object/create#stripe_elements-options-fonts">
            {str}
          </a>
        ),
      },
    },
  },
  {
    id: 'create-element',
    section: 'build-checkout-page',
    heading: {
      message: defineMessage({
        id:
          'docsShell.integrationBuilder.aap.web.html.steps.create-element.heading',
        defaultMessage: 'Create the Card Element',
      }),
    },
    body: {
      message: defineMessage({
        id:
          'docsShell.integrationBuilder.aap.web.html.steps.create-element.body',
        defaultMessage:
          'Create a Card Element and mount it to the placeholder <code>\'<div\'></code> in your payment form. This creates a single input that collects the card number, expiry date, CVC, and postal code. Elements displays localized placeholder text of the postal code field based on your customer\'s <a>browser locale</a> (e.g. showing "ZIP" for U.S. cardholders, "Postcode" for U.K. cardholders).',
      }),
      values: {
        a: (str) => (
          <a
            href="/docs/js/elements_object/create#stripe_elements-options-locale"
            target="_blank"
          >
            {str}
          </a>
        ),
        code: (label) => <code>{label}</code>,
      },
    },
  },
  {
    id: 'listen-for-change',
    section: 'build-checkout-page',
    heading: {
      message: defineMessage({
        id:
          'docsShell.integrationBuilder.aap.web.html.steps.listen-for-change.heading',
        defaultMessage: 'Optional: Surface card errors',
      }),
    },
    body: {
      message: defineMessage({
        id:
          'docsShell.integrationBuilder.aap.web.html.steps.listen-for-change.body',
        defaultMessage:
          'Listen to changes on the Card Element to immediately surface card errors (e.g. expiry date in the past) and disable the button if the Element is empty.',
      }),
    },
  },
  {
    id: 'form-submit',
    section: 'complete-the-payment',
    heading: {
      message: defineMessage({
        id:
          'docsShell.integrationBuilder.aap.web.html.steps.form-submit.heading',
        defaultMessage: 'Handle the submit event',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsShell.integrationBuilder.aap.web.html.steps.form-submit.body',
        defaultMessage:
          "Listen to the form's submit event to know when to confirm the payment through the Stripe API.",
      }),
    },
  },
  {
    id: 'complete-payment',
    section: 'complete-the-payment',
    heading: {
      message: defineMessage({
        id:
          'docsShell.integrationBuilder.aap.web.html.steps.complete-payment.heading',
        defaultMessage: 'Complete the payment',
      }),
    },
    body: {
      message: defineMessage({
        id:
          'docsShell.integrationBuilder.aap.web.html.steps.complete-payment.body',
        defaultMessage:
          'Call <code>confirmCardPayment()</code> passing along the client secret and Card Element, to complete the payment. Stripe automatically displays a modal if the card <a>requires authentication</a> like 3D Secure, where the customer must enter a passcode or other piece of identifying information to finalize the purchase.',
      }),
      values: {
        code: (str) => <code>{str}</code>,
        a: (str) => (
          <a href="https://www.youtube.com/watch?v=2kc-FjU2-mY" target="_blank">
            {str}
          </a>
        ),
      },
    },
  },
  {
    id: 'js-6',
    section: 'complete-the-payment',
    heading: {
      message: defineMessage({
        id: 'docsShell.integrationBuilder.aap.web.html.steps.js-6.heading',
        defaultMessage: 'Handle the API response',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsShell.integrationBuilder.aap.web.html.steps.js-6.body',
        defaultMessage:
          'If no error occurred, tell your customer the payment was successful! For any important post-payment actions (such as shipping packages, sending email receipts) we recommend <a>setting up a webhook</a>. If your customer’s card is declined, Stripe.js returns an error. Show that error message to your customer so they can try again with a different card',
      }),
      values: {
        a: (str) => (
          <a href="/docs/payments/handling-payment-events" target="_blank">
            {str}
          </a>
        ),
      },
    },
  },
  {
    id: 'run-server',
    file: 'server',
    section: 'test-the-integration',
    heading: {
      message: defineMessage({
        id:
          'docsShell.integrationBuilder.aap.web.html.steps.run-server.heading',
        defaultMessage: 'Run the application',
      }),
    },
  },
  {
    id: 'test-cards',
    section: 'test-the-integration',
    heading: {
      message: defineMessage({
        id:
          'docsShell.integrationBuilder.aap.web.html.steps.test-cards.heading',
        defaultMessage: 'Make a test payment',
      }),
    },
    testing: true,
    body: {
      message: defineMessage({
        id: 'docsShell.integrationBuilder.aap.web.html.steps.test-cards.body',
        defaultMessage:
          'Use a test card number to try your integration. These card numbers work in test mode with any CVC, postal code, and future expiry date. Stripe also has a set of <a>international test cards</a> to test specific postal code formats (e.g. only allow numerical values for U.S. zip codes).',
      }),
      values: {
        a: (str) => (
          <a href="/docs/testing#international-cards" target="_blank">
            {str}
          </a>
        ),
      },
    },
    Component: DefaultCards,
  },
  {
    extension: 'extra',
    id: 'email',
    heading: {
      message: defineMessage({
        id: 'docsShell.integrationBuilder.aap.web.html.steps.email.heading',
        defaultMessage: 'Send an email receipt',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsShell.integrationBuilder.aap.web.html.steps.email.body',
        defaultMessage:
          'Stripe can send an email receipt to your customer using your brand logo and color theme, configurable in <a>the Dashboard</a>.',
      }),
      values: {
        a: (str) => (
          <a
            href="https://dashboard.stripe.com/settings/branding"
            target="_blank"
          >
            {str}
          </a>
        ),
      },
    },
    steps: [
      {
        id: 'email-0',
        section: 'complete-the-payment',
        heading: {
          message: defineMessage({
            id:
              'docsShell.integrationBuilder.aap.web.html.steps.email-0.heading',
            defaultMessage: "Collect the customer's email address",
          }),
        },
        body: {
          message: defineMessage({
            id: 'docsShell.integrationBuilder.aap.web.html.steps.email-0.body',
            defaultMessage:
              'Add an input field to your payment form to collect an email address.',
          }),
        },
      },
      {
        id: 'email-1',
        section: 'complete-the-payment',
        heading: {
          message: defineMessage({
            id:
              'docsShell.integrationBuilder.aap.web.html.steps.email-1.heading',
            defaultMessage: 'Provide the email address to Stripe',
          }),
        },
        body: {
          message: defineMessage({
            id: 'docsShell.integrationBuilder.aap.web.html.steps.email-1.body',
            defaultMessage:
              'Pass the provided email address as the receipt_email value when completing the payment with <code>confirmCardPayment()</code>. Stripe will send an email receipt when the payment succeeds in live mode (but will not send one in test mode).',
          }),
          values: {
            code: (str) => <code>{str}</code>,
          },
        },
      },
    ],
  },
  {
    extension: 'extra',
    id: 'save-card',
    heading: {
      message: defineMessage({
        id: 'docsShell.integrationBuilder.aap.web.html.steps.save-card.heading',
        defaultMessage: 'Save card after payment',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsShell.integrationBuilder.aap.web.html.steps.save-card.body',
        defaultMessage:
          'SaaS or e-commerce businesses often save card details for recurring customers.',
      }),
    },
    steps: [
      {
        file: 'server',
        id: 'imports',
        section: 'extend-the-integration',
        heading: {
          message: defineMessage({
            id:
              'docsShell.integrationBuilder.aap.web.html.steps.imports.heading',
            defaultMessage: 'Import additional Stripe resources',
          }),
        },
      },
      {
        file: 'server',
        id: 'customer',
        section: 'extend-the-integration',
        heading: {
          message: defineMessage({
            id:
              'docsShell.integrationBuilder.aap.web.html.steps.customer.heading',
            defaultMessage: 'Create a customer',
          }),
        },
        body: {
          message: defineMessage({
            id: 'docsShell.integrationBuilder.aap.web.html.steps.customer.body',
            defaultMessage:
              'Stripe stores the card on a <a>Customer</a> object. Create a new Customer before creating a PaymentIntent. You can also store name, email, shipping address, and other details on the Customer.',
          }),
          values: {
            a: (str) => (
              <a href="/docs/api/customers" target="_blank">
                {str}
              </a>
            ),
          },
        },
      },
      {
        file: 'server',
        id: 'pass-customer',
        section: 'extend-the-integration',
        heading: {
          message: defineMessage({
            id:
              'docsShell.integrationBuilder.aap.web.html.steps.pass-customer.heading',
            defaultMessage: 'Add the customer to the PaymentIntent',
          }),
        },
        body: {
          message: defineMessage({
            id:
              'docsShell.integrationBuilder.aap.web.html.steps.pass-customer.body',
            defaultMessage:
              'Pass the Customer ID to the PaymentIntent and set <code>setup_future_usage</code> to <code>off_session</code>. <code>setup_future_usage</code> tells Stripe how you plan to use the card — certain regions, such as Europe and India, have requirements around reusing card details. <readMoreA>Read more</readMoreA> about optimizing <code>setup_future_usage</code>, otherwise simply set it to <code>off_session</code>. After the PaymentIntent succeeds, Stripe automatically <attachA>attaches</attachA> the card details (in a <paymentMethodA>PaymentMethod</paymentMethodA> object) to your Customer.',
          }),
          values: {
            code: (str) => <code>{str}</code>,
            readMoreA: (str) => (
              <a
                href="/docs/payments/save-and-reuse#web-create-payment-intent-off-session"
                target="_blank"
              >
                {str}
              </a>
            ),
            attachA: (str) => (
              <a href="/docs/api/payment_methods/attach" target="_blank">
                {str}
              </a>
            ),
            paymentMethodA: (str) => (
              <a href="/docs/api/payment_methods" target="_blank">
                {str}
              </a>
            ),
          },
        },
      },
      {
        file: 'server',
        id: 'charge',
        section: 'extend-the-integration',
        heading: {
          message: defineMessage({
            id:
              'docsShell.integrationBuilder.aap.web.html.steps.charge.heading',
            defaultMessage: 'Charge the card',
          }),
        },
        body: {
          message: defineMessage({
            id: 'docsShell.integrationBuilder.aap.web.html.steps.charge.body',
            defaultMessage: `When you're ready to charge the card again, create a new PaymentIntent with the Customer ID, the PaymentMethod ID of the card you want to charge, and set the <code>off_session</code> and <code>confirm</code> flags to true.`,
          }),
          values: {
            code: (str) => <code>{str}</code>,
          },
        },
      },
    ],
  },
]: StepsConfig);
